import * as fs from "node:fs";
import * as path from "node:path";
import { execFileSync } from "node:child_process";
import { LocalCliError } from "./errors.js";
import { parseReleasePin } from "./release-pin.js";
import type { SeedUpstream } from "./seed-manifest.js";
import {
  hydrateAgentsInto,
  fetchAgentContentForPinning,
  digestAgentContent,
  readDeclaredPersona,
  type AgentContentDeps,
  type AgentPin,
} from "./agent-content.js";

export const PIN_REL_PATH = "installer/release-pin.json";

/**
 * Hydrate every pinned agent into `root`, so that `root/personas/<p>/` and
 * `root/brain-seeds/<p>/` exist for consumers that know nothing about agent repos.
 *
 * This is the one operation the installer image build, the release cut, and local
 * development all perform — same code, same verification, same refusals.
 */
export async function syncAgentContent(args: {
  root: string;
  deps?: AgentContentDeps;
}): Promise<{ persona: string; repo: string; ref: string }[]> {
  const pinPath = path.join(args.root, PIN_REL_PATH);
  if (!fs.existsSync(pinPath)) {
    throw new LocalCliError({
      code: "RELEASE_PIN_MISSING",
      message: `No ${PIN_REL_PATH} under '${args.root}'.`,
      hint: "Run from an m8t checkout, or pass --root pointing at one.",
    });
  }
  const pin = parseReleasePin(fs.readFileSync(pinPath, "utf8"), pinPath);
  await hydrateAgentsInto(args.root, pin.agents, args.deps);
  return Object.values(pin.agents).map((a) => ({ persona: a.persona, repo: a.repo, ref: a.ref }));
}

/**
 * Where a seed's content came from, for the marker a new brain carries.
 *
 * Read from the PIN, deliberately: the pin is what this install DEPLOYED. Resolving
 * a live branch head at create time would record a commit the brain's content may
 * never have come from — provenance that looks authoritative and is wrong.
 *
 * Keyed on the PERSONA, not the agents map key: `m8t-labs/stacey` provides persona
 * `startup-advisor`, so keying on the key would record nothing for every brain
 * seeded from her.
 *
 * Best-effort and never throws. Provenance is a nice-to-have for a LATER refresh;
 * failing a brain creation over it would trade a real outcome for a hypothetical.
 * Returns undefined for a repo-local seed — that is a true answer, not a gap.
 */
export function upstreamForSeed(root: string, seedName: string): SeedUpstream | undefined {
  try {
    const pin = parseReleasePin(fs.readFileSync(path.join(root, PIN_REL_PATH), "utf8"), PIN_REL_PATH);
    const agent = Object.values(pin.agents).find((a) => a.persona === seedName);
    return agent ? { repo: agent.repo, ref: agent.ref } : undefined;
  } catch {
    return undefined;
  }
}

/**
 * A remedy for "persona/seed X is missing", when X is agent-sourced and simply has
 * not been hydrated into this checkout yet.
 *
 * Best-effort and never throws: this runs inside an error path, and a second
 * failure while explaining the first is how a clear problem becomes a confusing
 * one. Returns undefined when the pin is unreadable or does not name `persona` —
 * in which case the caller's own generic hint is the right one.
 */
export function agentContentHint(root: string, persona: string): string | undefined {
  try {
    const pin = parseReleasePin(fs.readFileSync(path.join(root, PIN_REL_PATH), "utf8"), PIN_REL_PATH);
    const agent = Object.values(pin.agents).find((a) => a.persona === persona);
    if (!agent) return undefined;
    return (
      `'${persona}' lives in ${agent.repo}, not in this repo — its content is generated, not committed. ` +
      "Run `pnpm agent-content:sync` to materialize it."
    );
  } catch {
    return undefined;
  }
}

/**
 * Resolve the current commit of a branch on a remote repo, without cloning.
 *
 * Uses `git ls-remote` rather than the API so it works with whatever credential
 * the operator's git already has — this runs at pin-bump time on a human's
 * machine, not in the install path.
 */
export function resolveRemoteRef(repo: string, branch: string): string {
  let out: string;
  try {
    out = execFileSync("git", ["ls-remote", `https://github.com/${repo}.git`, branch], {
      encoding: "utf8",
      stdio: ["ignore", "pipe", "pipe"],
    });
  } catch (e) {
    throw new LocalCliError({
      code: "AGENT_REF_RESOLVE_FAILED",
      message: `Could not resolve ${repo}#${branch}: ${(e as Error).message}`,
      hint: "Check the repo name, the branch, and that your git can read it (`gh auth setup-git` for private repos).",
    });
  }
  const sha = /^([0-9a-f]{40})\s/.exec(out.trim())?.[1];
  if (!sha) {
    throw new LocalCliError({
      code: "AGENT_REF_RESOLVE_FAILED",
      message: `${repo} has no branch '${branch}'.`,
    });
  }
  return sha;
}

/**
 * Re-derive a pin entry's `ref` + `contentSha` from a live branch.
 *
 * Two reads, in this order on purpose: resolve the branch to an immutable commit,
 * then fetch THAT commit and digest what it actually contains. The recorded digest
 * is therefore a statement about bytes that were read, not about a branch name that
 * could have moved between the two calls.
 *
 * The persona the repo declares is carried into the new entry rather than copied
 * from the old one, so a rename in the agent repo shows up as a pin diff the
 * operator reviews instead of a mismatch at install time.
 */
export async function derivePin(args: {
  current: AgentPin;
  branch: string;
  deps?: AgentContentDeps;
  /** Seam for tests; defaults to `git ls-remote`. */
  resolveRef?: (repo: string, branch: string) => string;
}): Promise<AgentPin> {
  const ref = (args.resolveRef ?? resolveRemoteRef)(args.current.repo, args.branch);
  const content = await fetchAgentContentForPinning(
    { repo: args.current.repo, ref, visibility: args.current.visibility },
    args.deps,
  );
  return {
    ...args.current,
    ref,
    persona: readDeclaredPersona(content),
    contentSha: digestAgentContent(content),
  };
}
